import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { useMemo } from "react";

import {
  AudienceAttendance,
  AudiencePositions,
  AudienceUserSummary,
  summarizeAudienceUsers,
  toAudienceStatus,
} from "./jobAudience";
import { JobAudience } from "./queries.graphql";
import { JobAudienceQuery, JobAudienceQueryVariables } from "./queries.types";

interface UseJobAudienceReturn {
  attendances: AudienceAttendance[];
  summaries: Map<string, AudienceUserSummary>;
  loading: boolean;
}

/**
 * Loads every attendance of the job, including cancelled and concept positions,
 * so the message dialog can split the job's people into audiences.
 * The query is skipped when no job id is given.
 */
export const useJobAudience = (jobId: string | undefined): UseJobAudienceReturn => {
  const { data, loading } = useQueryHandler<JobAudienceQuery, JobAudienceQueryVariables>(
    JobAudience,
    { id: jobId ?? "" },
    { skip: !jobId, fetchPolicy: "network-only" }
  );

  const attendances = useMemo<AudienceAttendance[]>(
    () =>
      data?.job?.shifts.flatMap(({ positions }) =>
        positions.flatMap(({ cancelled, attendances: positionAttendances }) =>
          positionAttendances.map(({ status, worker }) => ({
            userId: worker.id,
            status: toAudienceStatus(status),
            positions: cancelled ? AudiencePositions.Cancelled : AudiencePositions.Active,
          }))
        )
      ) ?? [],
    [data?.job]
  );

  const summaries = useMemo(() => summarizeAudienceUsers(attendances), [attendances]);

  return { attendances, summaries, loading };
};
